<template>
  <div class="statistics-view">
    <van-nav-bar
        title="Revenue Statistics"
        left-arrow
        fixed
        placeholder
        @click-left="onClickLeft"
        class="custom-navbar"
    />

    <van-list
        v-model:loading="loading"
        :finished="finished"
        loading-text="Loading..."
        finished-text="No more data"
        @load="onLoad"
        class="stats-list"
    >
      <div v-for="(item, index) in stats" :key="index" class="stats-card">
        <div class="card-header">
          <span class="stats-date">{{ item.createTime.substr(0, 10) }}</span>
          <van-tag plain type="primary" round>{{ item.currency }}</van-tag>
        </div>
        
        <div class="card-grid">
          <div class="grid-item">
            <span class="label">Orders</span>
            <span class="value">{{ item.payOrders }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Success</span>
            <span class="value success">{{ item.paySucceedOrders }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Payout</span>
            <span class="value">{{ item.payout }}</span>
          </div>
          <div class="grid-item">
            <span class="label">Profit</span>
            <span class="value profit">{{ formatProfit(item.profit) }}</span>
          </div>
        </div>
      </div>
    </van-list>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { getStatistics } from '@/utils/api'

const onClickLeft = () => history.back()
const stats = ref([])
const loading = ref(false)
const finished = ref(false)
const pageNum = ref(1)
const pageSize = ref(10)

const formatProfit = (profit) => {
  const num = parseFloat(profit || 0)
  if (num > 0) return `+${profit}`
  return String(profit ?? '0')
}

const fetchStats = async () => {
  try {
    const res = await getStatistics({
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      orderByColumn: 'create_time',
      isAsc: 'DESC'
    })

    if (res.rows && res.rows.length > 0) {
      // 检测 MyBatis reasonable=true 的情况：如果返回的数据第一条已存在于列表中，说明已经到底了
      const firstNewItemId = res.rows[0]?.id
      const isDuplicate = stats.value.length > 0 && stats.value.some(item => item.id === firstNewItemId)
      
      if (isDuplicate) {
        // 返回的数据与已有数据重复，说明已经到底了（reasonable=true导致返回了第一页）
        finished.value = true
      } else {
        // 正常情况：添加新数据
        stats.value = [...stats.value, ...res.rows]
        pageNum.value += 1
        // 如果返回的数据少于pageSize，说明已经到底了
        if (res.rows.length < pageSize.value) {
          finished.value = true
        }
      }
    } else {
      finished.value = true
    }
  } catch (error) {
    console.error('Error fetching stats:', error)
  } finally {
    loading.value = false
  }
}

const onLoad = () => {
  fetchStats()
}
</script>

<style scoped>
.statistics-view {
  background: #f8f9fa;
  min-height: 100vh;
  padding: 12px;
}

:deep(.custom-navbar) {
  background: #fff;
}

.stats-list {
  padding-bottom: 16px;
}

.stats-card {
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f2f2f7;
}

.stats-date {
  font-weight: 700;
  color: #1a1a1a;
  font-size: 13px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.grid-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.label {
  font-size: 11px;
  color: #8e8e93;
}

.value {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.value.success { color: #4cd964; }
.value.profit { color: #ff9f00; }
</style>
